import classNames from 'classnames';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { MultiUnderlyingSelector } from 'components/shared/underlyings/selector/multi';
import { UnderlyingsGridLegacy } from 'components/shared/underlyings/underlyingsGridLegacy';
import { useGenerateUnderlyingsGridForExcel } from 'hooks/underlyings/use-generate-underlyings-grid-for-excel';
import { useUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import { useCopyToExcel } from 'hooks/useCopyToExcel';
import {
  DEFAULT_STRIKE_METADATA,
  type UnderlyingsSearchEditableFieldData,
  type UnderlyingsSearchEditableFieldMetadata,
} from 'models/forms/fields';
import { Underlying } from 'models/underlying';
import type { UnderlyingStrike } from 'models/underlying/base';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { UnderlyingsFieldProps } from './UnderlyingsField.connect';
import { connectUnderlyings } from './UnderlyingsField.connect';
import { emptyUnderlyings, updateCutOff, updateStrike, updateWeight } from './utils';

const Component: FC<UnderlyingsFieldProps> = ({
  node: { id },
  defaultUnderlyingCodeType,
  workspaceType,
  tabIndex,
  tabId,
}) => {
  const { formatMessage } = useIntl();

  const [underlyings = emptyUnderlyings, setUnderlyings] = useData<UnderlyingsSearchEditableFieldData>(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const translationKey = useFieldTranslationKey(id);
  const {
    availableStrikeModes = [],
    hasCutOff,
    underlyingBlock,
  } = useFieldMetadata<UnderlyingsSearchEditableFieldMetadata>(id)!;

  const underlyingSearch = useUnderlyingSearchByFormId(tabId, underlyingBlock);

  const { underlyingsGroupedByAssetType, generateTableExport } = useGenerateUnderlyingsGridForExcel(underlyings);

  const assetTypes = useMemo(() => Array.from(underlyingsGroupedByAssetType.keys()), [underlyingsGroupedByAssetType]);

  const onAllUnderlyingsRemoved = useCallback(() => setUnderlyings([]), [setUnderlyings]);

  const onUnderlyingRemoved = useCallback(
    (idToRemove: string) =>
      setUnderlyings((underlyings || emptyUnderlyings).filter(underlying => underlying.id !== idToRemove)),
    [underlyings, setUnderlyings],
  );

  const onUnderlyingReplaced = useCallback(
    (idToReplace: string, newUnderlying: Underlying) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === idToReplace ? newUnderlying : underlying,
        ),
      ),
    [underlyings, setUnderlyings],
  );

  const onUnderlyingStrikeChanged = useCallback(
    (underlyingId: string, key: keyof UnderlyingStrike, value: unknown) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === underlyingId ? updateStrike(underlying, key, value) : underlying,
        ),
      ),
    [setUnderlyings, underlyings],
  );

  const onUnderlyingWeightChanged = useCallback(
    (underlyingId: string, value: number) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === underlyingId ? updateWeight(underlying, value) : underlying,
        ),
      ),
    [setUnderlyings, underlyings],
  );

  const onUnderlyingCutOffChanged = useCallback(
    (underlyingId: string, value: string) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === underlyingId ? updateCutOff(underlying, value) : underlying,
        ),
      ),
    [underlyings, setUnderlyings],
  );

  const placeholder = useMemo(() => formatMessage({ id: 'underlying.placeholder' }), [formatMessage]);

  const onCopyAll = useCopyToExcel(generateTableExport);

  return (
    <>
      <BlockOrientationFormGroup
        inputId={id}
        label={`form.${translationKey}` as any}
        className={classNames('mb-2', patchedFieldClassName)}
        workspaceType={workspaceType}
      >
        {!isReadOnly && (
          <MultiUnderlyingSelector
            id={id}
            {...underlyingSearch}
            onChange={setUnderlyings}
            underlyings={underlyings}
            placeholder={placeholder}
            hasErrors={isFieldInvalid}
            defaultUnderlyingCodeType={defaultUnderlyingCodeType}
            tabIndex={tabIndex}
          />
        )}
      </BlockOrientationFormGroup>
      <UnderlyingsGridLegacy
        fieldId={id}
        strikeMetadata={DEFAULT_STRIKE_METADATA}
        strikeModes={availableStrikeModes}
        hasCutOff={hasCutOff}
        formId={tabId}
        isReadOnly={isReadOnly}
        isUserInternal
        underlyings={underlyings}
        assetTypes={assetTypes}
        underlyingsGroupedByAssetType={underlyingsGroupedByAssetType}
        onAllUnderlyingsRemoved={onAllUnderlyingsRemoved}
        onUnderlyingRemoved={onUnderlyingRemoved}
        onUnderlyingReplaced={onUnderlyingReplaced}
        onUnderlyingStrikeChanged={onUnderlyingStrikeChanged}
        onUnderlyingWeightChanged={onUnderlyingWeightChanged}
        onUnderlyingCutOffChanged={onUnderlyingCutOffChanged}
        onCopyAll={onCopyAll}
        defaultUnderlyingCodeType={defaultUnderlyingCodeType}
      />
    </>
  );
};

Component.displayName = 'MultipleUnderlyingsField';

export const MultipleUnderlyingsField = connectUnderlyings(Component);
